import React, { useState } from 'react';
import { InvitationStatus, ProfileInvite2, SortDirection } from '@/types';
import { createDataTable } from '@/components/DataTable';
import { useAsyncStateWithIsLoading } from '@/utils/hooks';
import {
    ProfileInvitationSearchOptions,
    getProfileInvitations,
    getProfileInvitationsExportUrl,
    resendProfileInvitation,
} from '@/services/profile-invitations';
import { CustomLink, DateTimeDisplay, Toast, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { SlideToggle, Button } from '@ygtechnologies/rubus-ui';

export interface ProfileInvitationsTableProps {}

export const ResendInviteButton: React.FC<{ row: ProfileInvite2 }> = ({ row }) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = async () => {
        try {
            setIsLoading(true);
            await resendProfileInvitation(row.id);
            Toast.success('Resent invitation');
        } catch {
            Toast.error('Error resending invitation');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Button size="xs" disabled={row.status !== InvitationStatus.Sent} loading={isLoading} onClick={handleClick} variant="tertiary">
            Resend invitation
        </Button>
    );
};

const DataTable = createDataTable<ProfileInvite2>();
export const ProfileInvitationsTable: React.FC<ProfileInvitationsTableProps> = () => {
    const [tableState, setTableState] = useState(DataTable.getInitialState('inviteDate', SortDirection.Descending));
    const [options, setOptions] = useState<ProfileInvitationSearchOptions>({ includeAccepted: false });
    const [data, , , isLoading] = useAsyncStateWithIsLoading(
        () => getProfileInvitations(tableState, options),
        [tableState, options],
        null
    );

    const updateOptions = (changes: Partial<ProfileInvitationSearchOptions>) => {
        setOptions((prev) => ({ ...prev, ...changes }));
    };

    if (!data) {
        return <LoadingIndicator />;
    }

    return (
        <div>
            <SlideToggle
                checked={options.includeAccepted}
                onChange={(e) => updateOptions({ includeAccepted: e.target.checked })}>
                Include accepted invitations
            </SlideToggle>
            <DataTable
                exportUrl={getProfileInvitationsExportUrl(tableState, options)}
                hasSearchField
                isLoading={isLoading}
                data={data.items}
                onStateChange={setTableState}
                state={tableState}
                totalItemCount={data.totalCount}
                className="table style-3">
                <DataTable.Column header="Invitee email" field="inviteeEmail" sortable />
                <DataTable.Column header="Invitee name" field="inviteeName" sortable />
                <DataTable.Column
                    header="Profile"
                    field="profile"
                    sortable
                    renderer={({ value }) => <CustomLink to={`/profiles/${value.id}`}>{value.name}</CustomLink>}
                />
                <DataTable.Column
                    header="Invite date"
                    field="inviteDate"
                    sortable
                    renderer={({ value }) => (value ? <DateTimeDisplay value={value} /> : <>Unknown</>)}
                />
                <DataTable.Column
                    header="Accept date"
                    field="acceptDate"
                    sortable
                    renderer={({ value }) => (value ? <DateTimeDisplay value={value} /> : <>Not Accepted</>)}
                />
                <DataTable.Column
                    header="Invited by"
                    field="invitedByUser"
                    sortable
                    renderer={({ value }) =>
                        value ? <CustomLink to={`/users/${value.id}`}>{value.name}</CustomLink> : <>Admin</>
                    }
                />
                <DataTable.Column
                    header="Resend invitation"
                    field="status"
                    renderer={({ value, row }) => value !== InvitationStatus.Registered && <ResendInviteButton row={row} />}
                />
            </DataTable>
        </div>
    );
};
